import type { TFunction } from "i18next";
import { NurseTrainingStatus } from "../../../../utils/models";
import { type GapsStaffEntry, UNASSIGNED_LOCATION_NAME } from "../gapsDayModel";
import { joinLabelParts } from "../gapsLabels";

/**
 * The one line under a person's name in every staff sheet: their job, their unit, their own hours, and the marks the
 * row carries (charge, a training status). Their own hours only, never the shift window's as well: a punch rarely
 * matches its window to the minute, and two times side by side read as a conflict (APP-886). Built in one place so
 * the actions, More, note and hours steps all describe the person identically.
 */
export const staffSheetSubtitle = (t: TFunction, { entry, jobLabel, hoursLabel }: { entry: GapsStaffEntry; jobLabel: string; hoursLabel: string }): string => {
	const shift = entry.slot.nurseInShift;
	const parts = [
		jobLabel,
		entry.locationName === UNASSIGNED_LOCATION_NAME ? t("gaps.staffSheet.noUnit") : entry.locationName,
		hoursLabel,
		shift.charge ? t("gaps.staffSheet.charge") : "",
		trainingLabel(t, shift.trainingStatus),
	];
	return joinLabelParts(parts);
};

/** A training status as the sheets name it, or `""` for none (Regular, and Trainer, which is not a training mark). */
export const trainingLabel = (t: TFunction, status: NurseTrainingStatus): string => {
	switch (status) {
		case NurseTrainingStatus.Trainee:
			return t("gaps.staffSheet.trainee");
		case NurseTrainingStatus.Orientation:
			return t("gaps.staffSheet.orientation");
		case NurseTrainingStatus.Education:
			return t("gaps.staffSheet.education");
		case NurseTrainingStatus.Regular:
		case NurseTrainingStatus.Trainer:
			return "";
	}
};

/** The person's own hours: the row's label when they differ from the window's, else the window itself. */
export const staffHoursLabel = (entry: GapsStaffEntry, timeLabel: string): string => (entry.slot.hoursLabel === "" ? timeLabel : entry.slot.hoursLabel);
